'use client'

import React from 'react'
import { ArrowRight, ExternalLink, History, RefreshCw } from 'lucide-react'
import useSwr from 'swr'
import { Skeleton } from '@/components/ui/skeleton'
import { ESPO_URL } from '@/utils/constants'
import { formatAmount, type Pool } from '../lib/amm'
import { readHistory, type SwapRecord } from '../lib/history'
import { useAlkanes } from '../lib/store'
import { ACTIVITY_LIMIT, type ActivityEvent } from '../lib/useActivity'

function TxStatus({ txid }: { txid: string }) {
	const { data } = useSwr<{ confirmed: boolean }>(
		`https://mempool.space/api/tx/${txid}/status`,
		(u: string) => fetch(u).then((r) => r.json()),
		{ refreshInterval: (latest) => (latest?.confirmed ? 0 : 30_000) }
	)
	if (!data) {
		return <span className="text-muted-foreground">checking…</span>
	}
	return data.confirmed ? (
		<span className="text-success-ink">Confirmed</span>
	) : (
		<span className="text-warning-ink">Pending</span>
	)
}

/// Merge the two sources of truth about this wallet's trades.
///
/// Local records are written at broadcast time and carry the real QUOTE; the
/// chain only knows the `min_out` floor the route refused to go below. So a
/// txid present in both takes the local row, and a chain-only row renders its
/// output with a "≥" that says exactly what kind of number it is.
export function mergeHistory(
	local: SwapRecord[],
	events: ActivityEvent[],
	pools: Pool[]
): SwapRecord[] {
	const nameOf = (id: string): string => {
		if (id === 'BTC') {
			return 'BTC'
		}
		for (const p of pools) {
			if (p.token0.id === id && p.token0.name) {
				return p.token0.name
			}
			if (p.token1.id === id && p.token1.name) {
				return p.token1.name
			}
		}
		return id
	}
	const seen = new Set(local.map((h) => h.txid))
	const rows: SwapRecord[] = [...local]
	for (const e of events) {
		if (seen.has(e.txid)) {
			continue
		}
		const ts = (e.ts ?? 0) * 1000
		if (e.kind === 'swap' && e.path && e.path.length >= 2) {
			const sellId = e.path[0]
			const buyId = e.path[e.path.length - 1]
			rows.push({
				ts,
				txid: e.txid,
				sellId,
				sellName: nameOf(sellId),
				buyId,
				buyName: nameOf(buyId),
				amountInRaw: e.amount_in ?? '0',
				quotedOutRaw: e.min_out ?? '0',
				minOutRaw: e.min_out ?? '0',
				kind: 'swap',
				fromChain: true
			})
		} else if (e.kind === 'wrap' || e.kind === 'wrap_swap_leg') {
			rows.push({
				ts,
				txid: e.txid,
				sellId: 'BTC',
				sellName: 'BTC',
				buyId: '32:0',
				buyName: 'frBTC',
				amountInRaw: String(e.amount_sats ?? 0),
				quotedOutRaw: String(e.amount_sats ?? 0),
				minOutRaw: String(e.amount_sats ?? 0),
				kind: 'wrap',
				fromChain: true
			})
		} else if (e.kind === 'unwrap') {
			rows.push({
				ts,
				txid: e.txid,
				sellId: '32:0',
				sellName: 'frBTC',
				buyId: 'BTC',
				buyName: 'BTC',
				amountInRaw: e.amount_in ?? '0',
				quotedOutRaw: e.amount_in ?? '0',
				minOutRaw: e.amount_in ?? '0',
				kind: 'unwrap',
				fromChain: true
			})
		}
		// 'transfer' rows are moves, not trades — this is the trading record.
	}
	return rows.sort((a, b) => b.ts - a.ts)
}

/// This wallet's trades, newest first: the swaps made from this browser plus
/// the ones decoded from the address's own transactions. A list rather than a
/// table — six columns of tabular data do not fit a 320px viewport without a
/// sideways scroll, and a trade reads perfectly well stacked.
export default function ActivityList() {
	const pools = useAlkanes((s) => s.pools)
	const events = useAlkanes((s) => s.activity)
	const loggedIn = useAlkanes((s) => s.loggedIn)
	const isLoading = useAlkanes((s) => s.activityLoading)
	const unavailable = useAlkanes((s) => s.activityUnavailable)
	const refreshWallet = useAlkanes((s) => s.refreshWallet)
	const address = useAlkanes((s) => s.address)

	const [local, setLocal] = React.useState<SwapRecord[]>([])
	React.useEffect(() => {
		const load = () => setLocal(readHistory(address))
		load()
		window.addEventListener('alkanes-history-changed', load)
		return () => window.removeEventListener('alkanes-history-changed', load)
	}, [address])

	const rows = React.useMemo(() => mergeHistory(local, events, pools), [local, events, pools])

	return (
		<section aria-label="Activity" data-testid="alkanes-activity" className="flex flex-col gap-3">
			{unavailable && (
				<div
					role="status"
					data-testid="alkanes-activity-unavailable"
					className="flex items-center justify-between gap-3 rounded-2xl border border-warning/30 bg-warning/10 px-4 py-3"
				>
					<span className="text-xs leading-relaxed">
						On-chain trade history could not refresh. Browser-recorded trades remain below.
					</span>
					<button
						type="button"
						onClick={refreshWallet}
						className="alkanes-touch inline-flex shrink-0 items-center gap-1.5 rounded-lg text-xs font-semibold text-primary"
					>
						<RefreshCw className="size-3.5" aria-hidden="true" /> Retry
					</button>
				</div>
			)}
			<div className="overflow-hidden rounded-2xl bg-card">
				{isLoading && rows.length === 0 ? (
					<ul className="divide-y divide-border" aria-label="Loading activity">
						{[0, 1, 2].map((i) => (
							<li key={i} className="space-y-2 px-3 py-3.5 sm:px-4">
								<Skeleton className="h-4 w-40" />
								<Skeleton className="h-3 w-56" />
							</li>
						))}
					</ul>
				) : unavailable && rows.length === 0 ? (
					<div className="px-6 py-12 text-center">
						<History className="mx-auto size-8 text-muted-foreground" aria-hidden="true" />
						<span className="mt-3 block text-sm font-medium">Trade history unavailable</span>
						<span className="mx-auto mt-1 block max-w-[42ch] text-xs leading-relaxed text-muted-foreground">
							The API did not answer, so this screen cannot claim that there are no trades.
						</span>
					</div>
				) : rows.length === 0 ? (
					<div className="px-6 py-12 text-center">
						<History className="mx-auto size-8 text-muted-foreground" aria-hidden="true" />
						<span className="mt-3 block text-sm font-medium">No trades yet</span>
						<span className="mx-auto mt-1 block max-w-[42ch] text-xs leading-relaxed text-muted-foreground">
							{loggedIn
								? `Your alkane trades appear here — the swaps you make in this browser, plus the ones read back off chain from your last ${ACTIVITY_LIMIT} transactions.`
								: 'Connect a wallet and your alkane trades — from this browser and from your own on-chain history — appear here.'}
						</span>
					</div>
				) : (
					<ul className="divide-y divide-border" data-testid="alkanes-activity-list">
						{rows.map((h) => (
							<li key={h.txid} className="p-3 sm:px-4">
								<div className="flex items-baseline justify-between gap-3">
									<div className="flex min-w-0 items-center gap-1.5 text-sm font-semibold">
										{(h.kind === 'wrap' || h.kind === 'unwrap') && (
											<span className="capitalize shrink-0 rounded bg-secondary px-1.5 py-0.5 text-xxs font-semibold text-muted-foreground">
												{h.kind}
											</span>
										)}
										<span className="truncate">{h.sellName}</span>
										<ArrowRight
											className="size-3.5 shrink-0 text-muted-foreground"
											aria-hidden="true"
										/>
										<span className="truncate">{h.buyName}</span>
									</div>
									<span className="shrink-0 text-xxs font-medium">
										<TxStatus txid={h.txid} />
									</span>
								</div>

								<div className="mt-1 text-xs tabular-nums text-muted-foreground">
									{formatAmount(BigInt(h.amountInRaw), 4)} {h.sellName}
									{' → '}
									{/* A chain-decoded swap only knows the floor it would not
									    go below, so it never claims an exact fill. */}
									{h.fromChain && h.kind === 'swap' ? '≥ ' : ''}
									{formatAmount(BigInt(h.quotedOutRaw), 4)} {h.buyName}
									{h.kind === 'wrap' && h.intentBuyName && (
										<span> → {h.intentBuyName} (next step)</span>
									)}
								</div>

								<div className="mt-1 flex items-center gap-2 text-xxs tabular-nums text-muted-foreground">
									<span>{h.ts > 0 ? new Date(h.ts).toLocaleString() : 'awaiting a block'}</span>
									<span aria-hidden="true">·</span>
									<a
										href={`${ESPO_URL}/tx/${h.txid}`}
										target="_blank"
										rel="noreferrer"
										className="alkanes-touch inline-flex items-center gap-1 text-primary hover:underline"
									>
										{h.txid.slice(0, 8)}…
										<ExternalLink className="size-3" aria-hidden="true" />
										<span className="sr-only">View transaction on the explorer</span>
									</a>
								</div>
							</li>
						))}
					</ul>
				)}
			</div>

			{rows.length > 0 && (
				<span className="px-1 text-xxs leading-relaxed text-muted-foreground">
					Swaps made here keep the quote they were signed against. Trades read back off chain show
					the minimum the route accepted, not the fill — the chain does not record one.
				</span>
			)}
		</section>
	)
}
